import type { ConversationDetail, ConversationSummary, Me, Person, UserEventData } from '@sd/contracts';
import type { ClientState } from './state';

/** What `user.updated` says of someone: everything others see of them now (a server from before names sends only the photo). */
export type ProfileUpdate = Omit<Extract<UserEventData, { type: 'user.updated' }>, 'type'>;

const FIELDS = ['displayName', 'shortName', 'department', 'palette', 'status', 'revision'] as const;

/** The update a whole copy of the person stands for (a directory entry, a member, `Me`). */
export function profileOf(person: Person): ProfileUpdate {
  const { id, displayName, shortName, department, palette, status, avatar, revision } = person;
  return { userId: id, displayName, shortName, department, palette, status, ...(avatar ? { avatar } : {}), ...(revision !== undefined ? { revision } : {}) };
}

/** Whether `person` already shows a later change than `revision`; copies and events from servers before revisions never do. */
function isLater(person: Person, revision: number | undefined): boolean {
  return person.revision !== undefined && revision !== undefined && person.revision > revision;
}

/** The newer of two copies of a person: `incoming` unless `current` is known to be later. */
export function newerPerson<T extends Person>(current: T | undefined, incoming: T): T {
  return current && isLater(current, incoming.revision) ? current : incoming;
}

/**
 * `person` as `update` describes them; the same object when it changes nothing or the copy is
 * later. Fields the update leaves out stay as they are, except the photo: no `avatar`, no photo.
 */
function applied<T extends Person>(person: T, update: ProfileUpdate, force = false): T {
  if (person.id !== update.userId || (!force && isLater(person, update.revision))) return person;
  const fields: Partial<Record<(typeof FIELDS)[number], unknown>> = {};
  for (const field of FIELDS) if (update[field] !== undefined && update[field] !== person[field]) fields[field] = update[field];
  if (!Object.keys(fields).length && person.avatar?.version === update.avatar?.version) return person;
  const { avatar: _previous, ...rest } = person;
  return { ...rest, ...fields, ...(update.avatar ? { avatar: update.avatar } : {}) } as T;
}

/** A direct conversation is read-only while the other person is deactivated, as the server counts it. */
function withPeerState<T extends ConversationSummary>(c: T): T {
  if (c.kind !== 'direct' || !c.peer || (c.readOnlyReason !== null && c.readOnlyReason !== 'peer_deactivated')) return c;
  const gone = c.peer.status === 'deactivated';
  if (gone === (c.readOnlyReason === 'peer_deactivated')) return c;
  return { ...c, readOnly: gone, readOnlyReason: gone ? 'peer_deactivated' : null };
}

function mapChanged<T>(map: Record<string, T>, change: (value: T) => T): Record<string, T> {
  let out: Record<string, T> | null = null;
  for (const [key, value] of Object.entries(map)) {
    const next = change(value);
    if (next !== value) (out ??= { ...map })[key] = next;
  }
  return out ?? map;
}

/** Every copy of a person the store keeps, passed through `change`; slices it leaves alone keep their identity. */
function mapPeople(s: ClientState, change: (person: Person) => Person, changeMe: (me: Me) => Me): Partial<ClientState> {
  const list = (people: Person[]) => (people.some((p) => change(p) !== p) ? people.map(change) : people);
  const summary = <T extends ConversationSummary>(c: T): T => {
    const peer = c.peer && change(c.peer);
    const memberPreview = list(c.memberPreview);
    return peer === c.peer && memberPreview === c.memberPreview ? c : withPeerState({ ...c, peer, memberPreview });
  };
  const detail = (d: ConversationDetail): ConversationDetail => {
    const base = summary(d);
    const members = d.members.some((m) => change(m.person) !== m.person) ? d.members.map((m) => (change(m.person) === m.person ? m : { ...m, person: change(m.person) })) : d.members;
    return base === d && members === d.members ? d : { ...base, members };
  };
  const next: Partial<ClientState> = {};
  const people = mapChanged(s.people, change);
  if (people !== s.people) next.people = people;
  const me = s.me && changeMe(s.me);
  if (me !== s.me) next.me = me;
  const conversations = mapChanged(s.conversations, summary);
  if (conversations !== s.conversations) next.conversations = conversations;
  const details = mapChanged(s.details, detail);
  if (details !== s.details) next.details = details;
  return next;
}

/**
 * A `user.updated` in every copy of the person: the directory, the signed-in profile (and its role,
 * which only the person and company admins are told), conversation peers and previews, member lists.
 * A copy that already shows a later change keeps it. Slices without the person keep their identity.
 */
export function withProfile(s: ClientState, update: ProfileUpdate): Partial<ClientState> {
  const role = update.role;
  return mapPeople(
    s,
    (person) => applied(person, update),
    (me) => {
      const next = applied(me, update);
      return role !== undefined && me.id === update.userId && next.role !== role ? { ...next, role } : next;
    },
  );
}

/**
 * After an answer brought copies of people (a page, a member list, a profile): every copy of each
 * ends at the newest known, so an answer computed before a change that already arrived does not
 * bring the old name back. `before` is the directory as it was before the answer was stored.
 * `authoritative`: the database was restored, what it says now is current whatever came before.
 */
function withNewestPeople(s: ClientState, before: Record<string, Person>, incoming: readonly Person[], authoritative = false): Partial<ClientState> {
  const newest = new Map<string, Person>();
  for (const person of incoming) newest.set(person.id, authoritative ? person : newerPerson(newest.get(person.id) ?? before[person.id], person));
  if (!newest.size) return {};
  const updates = new Map([...newest].map(([id, person]) => [id, profileOf(person)]));
  const change = <T extends Person>(person: T): T => {
    const update = updates.get(person.id);
    return update ? applied(person, update, authoritative) : person;
  };
  return mapPeople(s, change, change);
}

/** `patch` from an answer that carries `people`, with every copy of them at the newest known (see withNewestPeople). */
export function withAnswer(s: ClientState, patch: Partial<ClientState>, people: readonly Person[], authoritative = false): Partial<ClientState> {
  return { ...patch, ...withNewestPeople({ ...s, ...patch }, s.people, people, authoritative) };
}

/** The people a conversation's summary or detail shows. */
export function peopleIn(c: ConversationSummary | ConversationDetail): Person[] {
  return [...(c.peer ? [c.peer] : []), ...c.memberPreview, ...('members' in c ? c.members.map((m) => m.person) : [])];
}

/** The signed-in profile from an answer: its role and sign-in details, and the newer of the two copies' public fields. */
export function newerMe(current: Me | null, incoming: Me): Me {
  return current && isLater(current, incoming.revision) ? { ...current, role: incoming.role, mfaEnabled: incoming.mfaEnabled } : incoming;
}
